import { FieldPath } from '@form-ts/core';
import { useFormField, useFormWatch } from '@form-ts/react';
import { pipe } from 'fp-ts/function';
import React, { useCallback } from 'react';
import { FormattedMessage } from 'src/migration/react-intl';
import { Input, InputProps } from 'src/modules/common/components/Input';
import { SearchIcon } from 'src/modules/common/icons/SearchIcon';
import { FormError } from 'src/modules/form/types/FormError';

type Props<TData> = Omit<InputProps,
| 'id'
| 'invalid'
| 'invalidText'
| 'value'
> & {
  readonly field: FieldPath<TData, FormError, string>;
  readonly renderError?: (error: FormError) => React.ReactNode;
};

export const SearchField: <TData>(props: Props<TData>) => React.ReactElement = ({
  size,
  field,
  onChange,
  onBlur,
  renderError = renderErrorDefault,
  ...props
}) => {
  const { error, meta, value } = useFormField(field);

  const submitted = useFormWatch(field.form, field.form.submitted.get);
  const invalid = error !== undefined && (meta.touched || submitted);

  const handleChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    field.form.change(pipe(
      field.form.currentState,
      field.value.set(event.target.value),
    ));

    onChange?.(event);
  }, [field, onChange]);

  const handleBlur = useCallback((event: React.FocusEvent<HTMLInputElement>) => {
    field.form.change(pipe(
      field.form.currentState,
      field.touched.set(true),
    ));

    onBlur?.(event);
  }, [field, onBlur]);

  return (
    <div className={props.className}>
      <Input
        {...props}
        id={`${field.form.name}.${field.path}`}
        name={field.path}
        invalid={invalid}
        invalidText={error && invalid ? renderError(error) : null}
        description={props.description === undefined ? (
          <FormattedMessage
            id="form/helper/maxLength"
            values={{ actualLength: value.length, maxLength: props.maxLength }}
          />
        ) : props.description}
        value={value}
        onChange={handleChange}
        onBlur={handleBlur}
        icon={<SearchIcon/>}
      />
    </div>
  );
};

const renderErrorDefault = (error: FormError): React.ReactNode => (
  <FormattedMessage id={`form/error/${error.code}`} values={error.context}/>
);
